<template>
  <b-card no-body class="border mb-2">
    <b-card-header class="pb-1">
      <div>
        <b-card-title class="mb-25">Permissions</b-card-title>
        <b-card-text class="text-muted small mb-0">
          What this client may do, per resource. Only actions a resource actually
          supports are offered — the list comes from the server's resource registry.
        </b-card-text>
      </div>
    </b-card-header>

    <b-card-body>
      <div v-if="!resources.length" class="text-muted small">
        No resources available.
      </div>

      <b-table-simple v-else responsive small class="mb-0">
        <b-thead>
          <b-tr>
            <b-th style="min-width: 200px">Resource</b-th>
            <b-th v-for="action in allActions" :key="action" class="text-center">
              {{ labelForAction(action) }}
            </b-th>
          </b-tr>
        </b-thead>

        <b-tbody>
          <b-tr v-for="resource in resources" :key="resource.resource">
            <b-td>
              <div class="font-weight-bold">
                {{ humanize(resource.resource) }}
                <b-badge
                  v-if="resource.soft_delete"
                  variant="light-secondary"
                  class="ml-50 font-weight-normal"
                  :title="deleteRuleHint"
                >
                  soft delete
                </b-badge>
              </div>
              <small class="text-muted">{{ resource.resource }}</small>
            </b-td>

            <b-td
              v-for="action in allActions"
              :key="`${resource.resource}-${action}`"
              class="text-center align-middle"
            >
              <b-form-checkbox
                v-if="resource.supported_actions.includes(action)"
                :checked="isChecked(resource.resource, action)"
                :disabled="disabled"
                @change="toggle(resource.resource, action, $event)"
              />
              <span v-else class="text-muted">&mdash;</span>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <b-alert v-if="hasSoftDeleteResources" variant="secondary" show class="mt-1 mb-0">
        <div class="alert-body small">
          <feather-icon icon="InfoIcon" class="mr-50" />
          On resources marked <strong>soft delete</strong> — the church's places —
          <strong>Delete</strong> hides the record rather than erasing it, applies only to
          records <em>this client created itself</em>, and is refused while anything still
          refers to the record. Granting it never puts the church's existing map at risk.
          <strong>Create</strong> and <strong>Update</strong> on those resources also
          enforce unique names and codes per level.
        </div>
      </b-alert>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardText,
  BCardTitle,
  BCardHeader,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BAlert,
  BBadge,
  BFormCheckbox,
} from 'bootstrap-vue'

// Every action the API's eCollectionActions enum defines. A resource that does not
// support one renders a dash rather than a checkbox.
const ALL_ACTIONS = [
  'CanList',
  'CanView',
  'CanAddNew',
  'CanUpdate',
  'CanDelete',
  'CanExport',
  'CanImport',
]

const ACTION_LABELS = {
  CanList: 'List',
  CanView: 'View',
  CanAddNew: 'Create',
  CanUpdate: 'Update',
  CanDelete: 'Delete',
  CanExport: 'Export',
  CanImport: 'Import',
}

export default {
  components: {
    BCard,
    BCardBody,
    BCardText,
    BCardTitle,
    BCardHeader,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BAlert,
    BBadge,
    BFormCheckbox,
  },
  props: {
    // [{ resource, actions: [] }]
    value: {
      type: Array,
      default: () => [],
    },
    // registry entries: [{ resource, supported_actions, dimensions }]
    resources: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      allActions: ALL_ACTIONS,
      deleteRuleHint:
        'Deleting hides the record instead of removing it, is limited to records this '
        + 'client created, and is blocked while anything still refers to it.',
    }
  },
  computed: {
    hasSoftDeleteResources() {
      return this.resources.some(resource => resource.soft_delete)
    },
  },
  methods: {
    labelForAction(action) {
      return ACTION_LABELS[action] || action
    },
    humanize(value) {
      return String(value)
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    isChecked(resource, action) {
      const grant = this.value.find(entry => entry.resource === resource)
      return Boolean(grant && grant.actions.includes(action))
    },
    toggle(resource, action, checked) {
      const next = this.value.map(entry => ({
        resource: entry.resource,
        actions: [...entry.actions],
      }))

      let grant = next.find(entry => entry.resource === resource)

      if (!grant) {
        grant = { resource, actions: [] }
        next.push(grant)
      }

      if (checked) {
        if (!grant.actions.includes(action)) grant.actions.push(action)
      } else {
        grant.actions = grant.actions.filter(entry => entry !== action)
      }

      // Drop resources left with no actions so the payload stays minimal and the
      // server never sees an empty grant it would have to interpret.
      this.$emit('input', next.filter(entry => entry.actions.length))
    },
  },
}
</script>
